/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import { Box } from "@mui/material";

function DeviceStatsCard({ bgColor = "white", variant = "h5", title = { fontWeight: "medium", text: "", subtext: "" }, percentage = { color: "success", text: "" }, icon = null, direction = "right", transparent = false }) {

  if (transparent) {
    return (
      <SoftBox p={2} alignItems="center">
        <SoftBox lineHeight={1} display="flex" flexDirection="row" alignItems="center">
          {icon != null && (
            <SoftBox variant="gradient">
              <Icon fontSize="large" color={icon.colour}
                sx={{
                  background: ({ functions: { linearGradient }, palette: { gradients } }) =>
                    linearGradient(gradients[icon.colour].main, gradients[icon.colour].state),
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >{icon.component}</Icon>
            </SoftBox>
          )}
          <Box ml={2}>
            <SoftBox display="flex" flexDirection="column">
              <SoftTypography
                variant={variant}
                fontWeight={title.fontWeight}
                textTransform="capitalize"
                color={"dark"}
                textGradient
              >
                {title.text}
              </SoftTypography>
              {title.subtext && (
                <SoftTypography
                  variant='body3'
                  fontWeight="regular"
                  textTransform="capitalize"
                  color={bgColor === "white" ? "dark" : "white"}
                >
                  {title.subtext}
                </SoftTypography>
              )}
            </SoftBox>
          </Box>
        </SoftBox>
      </SoftBox>
    )
  }
  else {
    return (
      <Card>
        <SoftBox bgColor={bgColor} variant="gradient">
          <SoftBox p={2} alignItems="center">
            <SoftBox ml={direction === "left" ? 2 : 0} lineHeight={1}>
              {icon != null && (
                <SoftBox variant="gradient">
                  <Icon fontSize="large" color={icon.colour}
                    sx={{
                      background: ({ functions: { linearGradient }, palette: { gradients } }) =>
                        linearGradient(gradients[icon.colour].main, gradients[icon.colour].state),
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >{icon.component}</Icon>
                </SoftBox>
              )}
              <SoftBox display="flex" flexDirection="column">
                <SoftTypography
                  variant={variant}
                  fontWeight="bold"
                  textTransform="capitalize"
                  color={bgColor === "white" ? "dark" : "white"}
                >
                  {title.text}
                </SoftTypography>
                {title.subtext && (
                  <SoftTypography
                    variant='body3'
                    fontWeight="regular"
                    textTransform="capitalize"
                    color={bgColor === "white" ? "dark" : "white"}
                  >
                    {title.subtext}
                  </SoftTypography>
                )}
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </SoftBox>
      </Card>
    );
  }
}

// Typechecking props for the DeviceStatsCard
DeviceStatsCard.propTypes = {
  bgColor: PropTypes.oneOf([
    "white",
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  variant: PropTypes.string,
  title: PropTypes.PropTypes.shape({
    fontWeight: PropTypes.oneOf(["light", "regular", "medium", "bold"]),
    text: PropTypes.string,
  }),
  percentage: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "white",
    ]),
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  icon: PropTypes.shape({
    colour: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
    component: PropTypes.string,
  }),
  direction: PropTypes.oneOf(["right", "left"]),
  transparent: PropTypes.bool,
};

export default DeviceStatsCard;
